/*!
 * MTAppCSVEditor.js
 *
 * Copyright (c) bit part LLC
 * https://bit-part/
 *
 * Since:   2019/12/06
 * Version: 1.2.4
 * Update:  2022/12/06
 *
 */
'use strict';

export function csvEditor(options) {
    if (!window.File || !window.FileReader) {
        return alert('ご使用のブラウザは動作対象外です。');
    }

    const $ = window.jQuery;
    const op = $.extend(
        {
            debug: false,
            selectors: null,
            tableClass: '',
            header: 0,
            fixedHeader: false,
            fixedHeight: 0, // number without 'px'
            rowHeader: 0,
            columnAction: true,
            rowAction: true
        },
        options
    );

    // 数値化しておく
    op.header = Number(op.header);

    // MT の「保存」「プレビュー」ボタンを取得
    const $mtActionButtonContainer = $('#entry-publishing-widget').find('button.publish.action.primary').closest('.row');
    // 保存の警告メッセージ
    const $mtSavingWarning = $('#bp-alert-below-action-bar').length
        ? $('#bp-alert-below-action-bar')
        : $mtActionButtonContainer
              .after('<div id="bp-alert-below-action-bar" class="alert alert-warning mt-4" role="alert" style="display: none;"></div>')
              .next();

    /**
     * デバッグモードの時にコンソールにメッセージを表示する。
     */
    const devMessage = function () {
        if (op.debug) {
            window.console.warn.apply(window.console, arguments);
        }
    };

    /**
     * ランダムな文字列を返す。
     *
     * @returns {string}
     */
    const randomString = function () {
        return Math.floor(Math.random() * 10000000000000000).toString(36);
    };

    /**
     * 保存の警告メッセージの表示状態を切り替える。
     *
     * @param type
     * @param text
     */
    const bpSavingAlert = function (type, text) {
        let editingCount = $mtSavingWarning.data('count') * 1 || 0;
        if (type === 'show') {
            editingCount++;
            $mtSavingWarning.text(text).show();
            $mtActionButtonContainer.find('button').prop('disabled', true);
        } else if (type === 'hide') {
            editingCount--;
            if (editingCount < 1) {
                $mtSavingWarning.hide();
                $mtActionButtonContainer.find('button').prop('disabled', false);
            }
        }
        $mtSavingWarning.data('count', editingCount);
    };
    /**
     * HTML をエスケープする。
     *
     * @param text
     * @returns {string|*}
     */
    const escapeHtml = function (text) {
        if (typeof text !== 'string') {
            return text;
        }
        return text
            .replace(/&/g, '&amp;')
            .replace(/'/g, '&#x27;')
            .replace(/`/g, '&#x60;')
            .replace(/"/g, '&quot;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    };

    /**
     * HTML をアンエスケープする。
     *
     * @param text
     * @returns {string|*}
     */
    // const unescapeHtml = function (text) {
    //   if (typeof text !== 'string') {
    //     return text;
    //   }
    //   return text
    //     .replace(/&#x27;/g, "'")
    //     .replace(/&#x60;/g, '`')
    //     .replace(/&quot;/g, '"')
    //     .replace(/&lt;/g, '<')
    //     .replace(/&gt;/g, '>')
    //     .replace(/&amp;/g, '&');
    // };

    /**
     * MTAppCSVEditor のベースの HTML を返す。
     *
     * @param suffix
     * @returns {string}
     */
    const baseHtml = function (uniqueId, op) {
        return `
        <div class="MTAppCSVEditor bp-readonly-mode${op.fixedHeader ? ' bp-table-fixed-header' : ''}">
            <div id="bp-table-container-${uniqueId}" data-unique-id="${uniqueId}" class="bp-table-container mt-table--outline" style="display: none;"></div>
            <div id="bp-action-bar-${uniqueId}" data-unique-id="${uniqueId}" class="bp-action-bar form-inline mt-5" style="display: none;">
                <div class="form-group mr-3">
                    <span class="custom-control-description mr-2">編集モード</span>
                    <input type="checkbox" class="mt-switch" id="bp-edit-${uniqueId}" data-type="bp-edit-mode">
                    <label for="bp-edit-${uniqueId}">編集モード</label>
                </div>
                <div class="form-group mr-4">
                    <span class="custom-control-description mr-2">全画面モード</span>
                    <input type="checkbox" class="mt-switch" id="bp-fullscreen-${uniqueId}" data-type="bp-fullscreen-mode">
                    <label for="bp-fullscreen-${uniqueId}">全画面モード</label>
                </div>
                <button type="button" class="bp-download btn btn-default btn-sm mr-3">ダウンロード</button>
                <button type="button" class="bp-download btn btn-default btn-sm mr-3" data-type="excel">ダウンロード（Excel用CSV）</button>
                <button type="button" class="bp-delete btn btn-danger btn-sm mr-3">データ削除</button>
            </div>
            <div id="bp-file-field-container-${uniqueId}" data-unique-id="${uniqueId}" class="form-group mt-4">
                <input id="bp-file-field-${uniqueId}" type="file" class="bp-file-field form-control-file">
            </div>
        </div>
    `;
    };

    /**
     * 各行のアクションメニューの HTML を返す。
     *
     * @returns {string}
     */
    const rowActionHtml = function () {
        const id = '' + randomString();
        const sortHandle = `<svg role="img" title="移動" class="mt-icon"><use xlink:href="${window.StaticURI.replace(
            /\/$/,
            ''
        )}/images/sprite.svg#ic_move" /></svg>`;
        const html = `
        <td class="bp-row-handle">${op.rowAction ? sortHandle : ''}</td>
        <td class="bp-row-actions">
            <div class="dropdown dropend">
                <button class="btn btn-default btn-sm dropdown-toggle" type="button" id="${id}" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false"${
                    op.rowAction ? '' : ' disabled'
                }></button>
                <div class="dropdown-menu" data-menu="row" aria-labelledby="${id}">
                    <a class="dropdown-item bp-row-action" data-action="insert-above" href="#">上に挿入</a>
                    <a class="dropdown-item bp-row-action" data-action="insert-below" href="#">下に挿入</a>
                    <a class="dropdown-item bp-row-action" data-action="move-above" href="#">上に移動</a>
                    <a class="dropdown-item bp-row-action" data-action="move-below" href="#">下に移動</a>
                    <a class="dropdown-item bp-row-action" data-action="delete" href="#">削除</a>
                </div>
            </div>
        </td>`;
        return html;
    };

    /**
     * 各列のアクションメニューの HTML を返す。
     *
     * @returns {string}
     */
    const columnActionHtml = function (index, thClass) {
        const id = '' + randomString();
        const html = `
        <th class="bp-column-actions column-${index} ${thClass}">
            <div class="dropdown">
                <button class="btn btn-default btn-sm dropdown-toggle" type="button" id="${id}" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false"${
                    op.columnAction ? '' : ' disabled'
                }></button>
                <div class="dropdown-menu" data-menu="column" aria-labelledby="${id}">
                    <a class="dropdown-item bp-column-action" data-action="insert-right" data-index="${index}" href="#">右に挿入</a>
                    <a class="dropdown-item bp-column-action" data-action="insert-left" data-index="${index}" href="#">左に挿入</a>
                    <a class="dropdown-item bp-column-action" data-action="move-right" data-index="${index}" href="#">右に移動</a>
                    <a class="dropdown-item bp-column-action" data-action="move-left" data-index="${index}" href="#">左に移動</a>
                    <a class="dropdown-item bp-column-action" data-action="delete" data-index="${index}" href="#">削除</a>
                </div>
            </div>
        </th>`;
        return html;
    };

    /**
     * td の HTML を返す。
     *
     * @param index
     * @param value
     * @returns {string}
     */
    const tdHtml = function (tdClass, value) {
        value = value || '';
        return `<td class="${tdClass}"><pre class="bp-data">${escapeHtml(value)}</pre></td>`;
    };

    /**
     * テーブル1行分のデータの配列を tr タグに変換する。
     *
     * @param array
     * @param trClass
     * @returns {string}
     */
    const arrayToTrHtml = function (array, columnCount, trClass) {
        trClass = trClass ? ' class="' + trClass + '"' : '';
        let tr = rowActionHtml();
        if (array.length !== columnCount) {
            for (let i = 0, l = columnCount - array.length; i < l; i++) {
                array.push('');
            }
        }
        for (let i = 0, l = array.length; i < l; i++) {
            array[i] = array[i].replace(/^"([^\0]*)"$/g, '$1');
            let tdClass = 'column-' + i;
            if (i < op.rowHeader) {
                tdClass += ' bp-th';
            }
            tr += tdHtml(tdClass, array[i]);
        }
        return `<tr${trClass}>${tr}</tr>`;
    };

    /**
     * tr セットの HTML を受け取って table タグを返す。
     *
     * @param trHtml
     * @param columnCount
     * @param tableClass
     * @returns {string}
     */
    const createTableHtml = function (trHtml, columnCount, tableClass, rowHeader) {
        let theadInnerHtml =
            '<th class="bp-row-handle"><div class="bp-w-30"></div></th><th class="bp-row-actions"><div class="bp-w-30"></div></th>';
        for (let i = 0; i < columnCount; i++) {
            const thClass = i < rowHeader ? ' bp-th' : '';
            theadInnerHtml += columnActionHtml(i, thClass);
        }
        return `
            <table class="table table-striped ${tableClass}">
                <thead>${theadInnerHtml}</thead>
                <tbody>${trHtml.join('')}</tbody>
            </table>
        `;
    };

    /**
     * table を DOM に入れる。
     *
     * @param $tableContainer
     * @param tableHtml
     */
    const placeTable = function ($tableContainer, tableHtml) {
        const uniqueId = $tableContainer.attr('data-unique-id');
        const containerId = `bp-table-container-${uniqueId}`;
        $('#bp-action-bar-' + uniqueId).show();
        $('#bp-file-field-' + uniqueId).val('');
        $tableContainer.html(tableHtml).show();

        // ウィンドウ枠固定の時にアクションメニューの位置を調整する
        if (op.fixedHeader && op.rowHeader > 0) {
            $('.dropdown').on('shown.bs.dropdown', function () {
                const $menu = $(this).find('.dropdown-menu');
                const type = $menu.attr('data-menu');
                if (type === 'row') {
                    const $columns = $(this).closest('.bp-row-actions').nextAll('.bp-th');
                    let width = 8;
                    $columns.each(function () {
                        width += $(this).width();
                    });
                    $menu.css({
                        marginLeft: width + 'px'
                    });
                }
            });
            if (op.header > 0 || op.rowHeader > 1) {
                const $tbody = $(`#${containerId} tbody`);
                let rowHeightSum = 0;
                let styleContent = '';
                for (let i = 0; i < op.header; i++) {
                    let rowHeight = $tbody.find('tr:nth-child(' + (i + 1) + ') .column-0').height();
                    rowHeight = Math.round(rowHeight);
                    rowHeightSum += rowHeight;
                    styleContent += `#${containerId} tbody tr:nth-child(${i + 2}) td {top: ${rowHeightSum}px;}`;

                    if (i === 0) {
                        styleContent += `.MTAppCSVEditor.bp-table-fixed-header.bp-edit-mode #${containerId} tbody tr:nth-child(1) td {top: 40px;}`;
                    }

                    styleContent += `.MTAppCSVEditor.bp-table-fixed-header.bp-edit-mode #${containerId} tbody tr:nth-child(${
                        i + 2
                    }) td {top: ${rowHeightSum + 40}px;}`;
                }
                // for (let i = 0; i < op.rowHeader; i++) {
                //     let columnWidth = $tr.find('.column-' + i).width();
                //     columnWidth = Math.round(columnWidth);
                //     styleContent += `#${containerId} .column-${i + 1} {left: ${columnWidth}px;}`;
                //     styleContent += `.MTAppCSVEditor.bp-table-fixed-header.bp-edit-mode #${containerId} .column-${i + 1} {left: ${
                //         columnWidth + 60
                //     }px;}`;
                // }
                styleContent += `#${containerId} .dropdown-menu[data-menu="column"] {top: ${rowHeightSum}px !important;}`;
                const styleTag = document.createElement('style');
                styleTag.type = 'text/css';
                styleTag.innerText = styleContent;
                document.getElementsByTagName('head').item(0).appendChild(styleTag);
            }
        }
        // END ウィンドウ枠固定の時にアクションメニューの位置を調整する
    };

    /**
     * CSV データを table タグに変換する。
     *
     * @param $textarea
     * @param $tableContainer
     * @param csvData
     * @returns {{result: *, html: *}}
     */
    const csvToTableWithSaving = function ($textarea, $tableContainer, csvData) {
        const csvArray = [];
        let trHtml = [];
        let columnCount = 0;

        // 改行で区切って配列に変換
        const rows = csvData.split(/(\r\n|\n|\r)+(?=(?:[^"]*"[^"]*")*[^"]*$)/);

        // すべての行をループ処理
        let counter = 0;
        for (let i = 0, l = rows.length; i < l; i++) {
            if (/^(\r\n|\n|\r)+$/.test(rows[i])) {
                continue;
            }
            // ダブルクオーテーション内のカンマ以外のカンマで区切って配列に変換
            const columns = rows[i].trim().split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/);
            // セルの中の "" を " に変換
            for (let i = 0, l = columns.length; i < l; i++) {
                columns[i] = columns[i]
                    .replace(/^"|"$/g, '__QQ__')
                    .replace(/""/g, '"')
                    .replace(/__QQ__/g, '"');
            }
            // 1行のカラム数をセット
            if (i === 0) {
                columnCount = columns.length;
            }
            // テーブル1行分のデータの配列を tr タグに変換して table の HTML に結合
            const trClass = counter < op.header ? 'bp-sortable-disabled' : 'bp-sortable';
            trHtml.push(arrayToTrHtml(columns, columnCount, trClass));
            // CSV 全体の配列に追加
            csvArray.push(columns);
            counter++;
        }

        // テキストエリアに CSV 全体の配列を JSON に変換して保存
        $textarea.val(mtapp.toJSONString(csvArray));

        // table を作成
        const tableHtml = createTableHtml(trHtml, columnCount, op.tableClass, op.rowHeader);
        placeTable($tableContainer, tableHtml);

        // CSV 全体の配列と table タグを返却
        return {
            html: tableHtml,
            csvArray: csvArray
        };
    };

    /**
     * CSV 全体の配列から table を作成する。
     *
     * @param $tableContainer
     * @param csvArray
     * @returns {boolean}
     */
    const arrayToTable = function ($tableContainer, csvArray) {
        let trHtml = [];
        let columnCount = 0;
        for (let i = 0, l = csvArray.length; i < l; i++) {
            if (i === 0) {
                columnCount = csvArray[i].length;
            }
            const trClass = i < op.header ? 'bp-sortable-disabled' : 'bp-sortable';
            trHtml.push(arrayToTrHtml(csvArray[i], columnCount, trClass));
        }
        placeTable($tableContainer, createTableHtml(trHtml, columnCount, op.tableClass, op.rowHeader));
        return true;
    };

    /**
     * table を CSV 全体の配列に変換するして配列を保存する。
     *
     * @param $textarea
     * @param $tableContainer
     * @returns {[]}
     */
    const tableToArrayWithSaving = function ($textarea, $tableContainer) {
        const csvArray = [];

        // tr ごとにループ処理
        $tableContainer.find('tbody tr').each(function () {
            // td のデータを各行の配列に追加
            const columns = [];
            $(this)
                .find('.bp-data')
                .each(function () {
                    columns.push(this.textContent);
                });
            // 各行の配列を CSV 全体の配列に追加
            csvArray.push(columns);
        });

        // テキストエリアに CSV 全体の配列を JSON に変換して保存
        if (csvArray.length) {
            $textarea.val(mtapp.toJSONString(csvArray));
        }

        // CSV 全体の配列を返却
        return csvArray;
    };

    /**
     * 2重配列のデータを CSV 文字列に変換する。
     *
     * @param array
     * @param delimiter
     * @returns {string}
     */
    const arrayToCsv = function (array) {
        if (!Array.isArray(array)) {
            alert('Invalid Array');
            return '';
        }

        const rows = [];
        for (let i = 0, l = array.length; i < l; i++) {
            const row = array[i];
            // セルの中に " がある場合は "" に変換する
            for (let j = 0, k = row.length; j < k; j++) {
                row[j] = row[j].replace(/"/g, '""');
            }
            // セルの値を " で囲って、1行分のデータを文字列に変換する
            rows.push('"' + row.join('","') + '"');
        }
        return rows.join('\n');
    };

    /**
     * CSV をダウンロードする。
     *
     * @param data
     * @param filename
     * @param addBom
     */
    const exportCsv = function (data, filename, addBom) {
        // 保存された二重配列を CSV 文字列に変換
        const csv = arrayToCsv(data);

        // 出力ファイル名
        filename = filename || 'export.csv';
        devMessage('ダウンロードされる ' + filename + ' データ' + '\n\n' + csv);

        // BLOBに変換
        const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
        const blobPart = addBom ? [bom, csv] : [csv];
        const blob = new Blob(blobPart, { type: 'text/csv;charset=utf-8;' });

        if (navigator.msSaveBlob) {
            // for IE 10+
            navigator.msSaveBlob(blob, filename);
        } else {
            //anchorを生成してclickイベントを呼び出す。
            const link = document.createElement('a');
            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', filename);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    };

    return this.each(function () {
        // 適用するフィールドは `textarea` 限定
        if (this.tagName.toLowerCase() !== 'textarea') {
            console.error('MTAppCSVEditor は textarea に適用してください。');
            return;
        }

        const uniqueId = randomString();
        const $textarea = $(this);
        const $container = $textarea.after(baseHtml(uniqueId, op)).next('.MTAppCSVEditor');
        const $tableContainer = $container.find('.bp-table-container');
        const $actionBar = $container.find('.bp-action-bar');
        const $fileField = $container.find('.bp-file-field');

        // 元のテキストエリアの調整
        $textarea.prop('readonly', true);
        if (op.debug) {
            $textarea.css({
                marginBottom: '20px',
                height: '300px',
                fontFamily: 'monaco, monospace'
            });
        } else {
            $textarea.hide();
        }
        // END 元のテキストエリアの調整

        // ウィンドウ枠固定の場合に高さを固定する
        if (op.fixedHeader && op.fixedHeight > 0) {
            $tableContainer.css({
                height: op.fixedHeight + 'px'
            });
        }
        // END ウィンドウ枠固定の場合に高さを固定する

        // コンテナーに追加用のインデックスをセット
        $container.data('additionalIndex', 10000);
        // END コンテナーに追加用のインデックスをセット

        // 編集モードのとき
        $container.on('click', 'td', function () {
            if ($container.hasClass('bp-edit-mode') && !$(this).find('textarea').length) {
                const $td = $(this);
                const width = $td.width();
                const height = $td.height();
                $td.width(width);
                const $bpData = $td.find('.bp-data').hide();
                if ($bpData.length) {
                    const html = $bpData[0].innerHTML;
                    const $bpDataEditor = $bpData
                        .after(`<textarea class="bp-data-editor" style="width: ${width}px; height: ${height}px;">${html}</textarea>`)
                        .next();
                    $bpDataEditor
                        .on('blur', function () {
                            $bpData.before('<pre class="bp-data">' + escapeHtml(this.value) + '</pre>');
                            $(this).remove();
                            $bpData.remove();
                            $td.removeAttr('style');
                        })
                        .trigger('focus');
                }
            }
        });
        // END 編集モードのとき

        // 各行のアクションメニューのイベントをセット
        $container.on('click', '.bp-row-action', function (e) {
            e.preventDefault();
            const type = $(this).attr('data-action');
            const $parentTr = $(this).closest('tr');
            if (type === 'delete') {
                $parentTr.remove();
            } else {
                const columnCount = $parentTr.find('td').length - 2; // アクション関係の td の数を引く
                const newRowArray = [];
                // IE が fill が使えないためループして空文字で埋める
                for (let i = 0; i < columnCount; i++) {
                    newRowArray.push('');
                }
                const newRow = arrayToTrHtml(newRowArray, columnCount, 'bp-sortable bp-additional-row mt-table__highlight--current');
                if (type === 'insert-above') {
                    $parentTr.before(newRow);
                } else if (type === 'insert-below') {
                    $parentTr.after(newRow);
                } else if (type === 'move-above') {
                    $parentTr.addClass('mt-table__highlight--current').prev().before($parentTr);
                } else if (type === 'move-below') {
                    $parentTr.addClass('mt-table__highlight--current').next().after($parentTr);
                }
                window.setTimeout(function () {
                    $container.find('.mt-table__highlight--current').removeClass('mt-table__highlight--current');
                }, 1000);
            }
            $(this).parent().prev().trigger('click');
            return false;
        });
        // END 各行のアクションメニューのイベントをセット

        // 各列のアクションメニューのイベントをセット
        $container.on('click', '.bp-column-action', function (e) {
            e.preventDefault();
            const type = $(this).attr('data-action');
            const index = $(this).attr('data-index');
            const $cellCollection = $container.find('.column-' + index);
            if (type === 'delete') {
                $cellCollection.remove();
            } else {
                const additionalIndex = $container.data('additionalIndex') * 1 + 1;
                if (type === 'insert-right') {
                    $cellCollection.each(function () {
                        if (this.tagName.toLowerCase() === 'th') {
                            $(this).after(columnActionHtml(additionalIndex, ''));
                        } else {
                            const tdClass = 'column-' + additionalIndex + ' mt-table__highlight--current';
                            $(this).after(tdHtml(tdClass, ''));
                        }
                    });
                } else if (type === 'insert-left') {
                    $cellCollection.each(function () {
                        if (this.tagName.toLowerCase() === 'th') {
                            $(this).before(columnActionHtml(additionalIndex, ''));
                        } else {
                            const tdClass = 'column-' + additionalIndex + ' mt-table__highlight--current';
                            $(this).before(tdHtml(tdClass, ''));
                        }
                    });
                } else if (type === 'move-right') {
                    $cellCollection.addClass('mt-table__highlight--current');
                    $cellCollection.each(function () {
                        const $this = $(this);
                        $this.next().after($this);
                    });
                } else if (type === 'move-left') {
                    $cellCollection.addClass('mt-table__highlight--current');
                    $cellCollection.each(function () {
                        const $this = $(this);
                        $this.prev().before($this);
                    });
                }
                $container.data('additionalIndex', additionalIndex);
                window.setTimeout(function () {
                    $container.find('.mt-table__highlight--current').removeClass('mt-table__highlight--current');
                }, 1000);
            }
        });
        // END 各列のアクションメニューのイベントをセット

        // 編集モードスイッチのイベントをセット
        $actionBar.find('.mt-switch').on('change', function () {
            const type = $(this).attr('data-type');
            if (type === 'bp-edit-mode') {
                if ($(this).prop('checked')) {
                    $container.addClass(type);
                    $container.removeClass('bp-readonly-mode');
                    $mtActionButtonContainer.find('button').prop('disabled', true);
                    bpSavingAlert('show', 'CSV エディタの編集モードを解除してください。');
                    if (op.rowAction) {
                        $tableContainer.find('tbody').sortable({
                            items: '> tr.bp-sortable',
                            handle: '.bp-row-handle'
                        });
                    }
                } else {
                    $container.removeClass(type);
                    $container.addClass('bp-readonly-mode');
                    $container.find('textarea.bp-data-editor').trigger('blur');
                    tableToArrayWithSaving($textarea, $tableContainer);
                    // MT の保存ボタン等を押せるようにする
                    bpSavingAlert('hide');
                    $actionBar.find('.bp-download').prop('disabled', false);
                    const $tbody = $tableContainer.find('tbody');
                    if ($tbody.data('ui-sortable')) {
                        $tbody.sortable('destroy');
                    }
                }
            } else if (type === 'bp-fullscreen-mode') {
                if ($(this).prop('checked')) {
                    $container.addClass(type);
                    $container.css({
                        width: $(window).width() + 'px',
                        height: $(window).height() + 'px'
                    });
                    if (op.fixedHeader) {
                        $tableContainer.css({
                            height: $(window).height() - 150 + 'px'
                        });
                    }
                    $('#mtapp-sidebar-collapse').hide();
                    $('body').css('overflow', 'hidden');
                } else {
                    $container.removeClass(type).removeAttr('style');
                    if (op.fixedHeader) {
                        $tableContainer.css({
                            height: op.fixedHeight > 0 ? op.fixedHeight + 'px' : 'auto'
                        });
                    }
                    $('#mtapp-sidebar-collapse').show();
                    $('body').css('overflow', 'auto');
                }
            }
        });
        // END 編集モードスイッチのイベントをセット

        // 削除ボタンのイベントをセット
        $actionBar.find('.bp-delete').on('click', function () {
            if (confirm('データを削除しますか？')) {
                $actionBar.hide().next().show();
                $tableContainer.find('table').fadeOut(function () {
                    $(this).remove();
                    $('#bp-edit-' + uniqueId)
                        .prop('checked', false)
                        .trigger('change');
                    bpSavingAlert('hide');
                    $textarea.val('');
                    $tableContainer.hide();
                });
            }
        });
        // END 削除ボタンのイベントをセット

        // CSV をダウンロード
        $actionBar.find('.bp-download').on('click', function () {
            const value = $textarea.val();
            if (!value) {
                alert('保存できるデータがありません。');
                return;
            }
            const type = $(this).attr('data-type');
            const suffix = type === 'excel' ? '-xlsx' : '';
            const bom = type === 'excel';
            const filename = $textarea.attr('name').replace(/\W/g, '') + suffix + '.csv';
            const data = JSON.parse(value);
            exportCsv(data, filename, bom);
            return true;
        });
        // END CSV をダウンロード

        // 保存データのレンダリング
        const savedCsv = $textarea.val();
        if (savedCsv) {
            arrayToTable($tableContainer, JSON.parse(savedCsv));
        }
        // END 保存データのレンダリング

        // ファイルの読み込みとレンダリング
        $fileField.on({
            change: function () {
                const file = this.files[0];
                if (!file) {
                    return;
                }
                const type = file.type;
                devMessage(file.name + ' が選択されました（ファイル・タイプ：' + type + '）');

                const fileType = ['text/csv', 'application/csv', 'application/vnd.ms-excel', 'application/octet-stream'];
                if (fileType.indexOf(type) === -1) {
                    alert('CSV ファイルを選択してください。');
                    return false;
                }

                const reader = new FileReader();
                reader.readAsText(file);
                reader.onload = function () {
                    if (reader.readyState === FileReader.DONE) {
                        devMessage('読み込まれた内容（変換前）', reader.result);
                        const csv = reader.result.trim();
                        const result = csvToTableWithSaving($textarea, $tableContainer, csv);
                        devMessage('読み込まれた内容（変換後）', result);
                    } else {
                        return alert('ファイルの読み込みに失敗しました（readyState: ' + reader.readyState + '）');
                    }
                };
            }
        });
    });
    // END ファイルの読み込みとレンダリング
}
